import { useId, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';

import {
    LuLogOut,
    LuMonitor,
    LuMoon,
    LuSun,
} from 'react-icons/lu';
import { setThemePreference, useTheme } from '@/shared/model/theme';
import classes from './UserMenu.module.scss';

const themeOptions = [
    {
        value: 'system',
        label: 'Системная',
        Icon: LuMonitor,
    },
    {
        value: 'light',
        label: 'Светлая',
        Icon: LuSun,
    },
    {
        value: 'dark',
        label: 'Тёмная',
        Icon: LuMoon,
    },
] as const;

interface UserMenuProps {
    login: string;
    role: string;
    logout: () => void;
}

/** Показывает профиль сотрудника и локальные настройки оформления */
export const UserMenu = ({ login, role, logout }: UserMenuProps) => {
    const panelRef = useRef<OverlayPanel>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const firstActionRef = useRef<HTMLButtonElement>(null);
    const lastActionRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const panelId = useId();
    const { preference } = useTheme();
    const name = login.trim() || 'Пользователь';
    const roleName = role.trim() || 'Сотрудник';
    const words = name.split(/\s+/);

    const initials = (words.length > 1
        ? `${words[0].slice(0, 1)}${words[1].slice(0, 1)}`
        : name.slice(0, 2)).toLocaleUpperCase('ru-RU');

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={classes.avatarButton}
                aria-label={`Меню пользователя: ${name}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={panelId}
                title={`${name} · ${roleName}`}
                onClick={(event) => panelRef.current?.toggle(event)}
            >
                <span aria-hidden="true">{initials}</span>
            </button>
            <OverlayPanel
                ref={panelRef}
                id={panelId}
                className={classes.panel}
                dismissable
                closeOnEscape
                onShow={() => {
                    setOpen(true);
                    firstActionRef.current?.focus();
                }}
                onHide={() => {
                    setOpen(false);
                }}
                pt={{
                    root: {
                        role: 'dialog',
                        'aria-label': 'Профиль и настройки',
                        onKeyDown: (event) => {
                            if (event.key === 'Escape') {
                                panelRef.current?.hide();
                                buttonRef.current?.focus();
                            }

                            if (event.key !== 'Tab') return;

                            if (event.shiftKey && document.activeElement === firstActionRef.current) {
                                event.preventDefault();
                                lastActionRef.current?.focus();
                            } else if (!event.shiftKey && document.activeElement === lastActionRef.current) {
                                event.preventDefault();
                                firstActionRef.current?.focus();
                            }
                        },
                    },
                }}
            >
                <div className={classes.identity}>
                    <span
                        className={classes.avatar}
                        aria-hidden="true"
                    >
                        {initials}
                    </span>
                    <div className={classes.userDetails}>
                        <strong className={classes.name}>{name}</strong>
                        <span className={classes.role}>{roleName}</span>
                    </div>
                </div>
                <div
                    className={classes.themeControl}
                    role="group"
                    aria-label="Тема оформления"
                >
                    <span className={classes.sectionTitle}>Тема оформления</span>
                    <div className={classes.themeOptions}>
                        {themeOptions.map(({ value, label, Icon }) => (
                            <button
                                key={value}
                                ref={value === 'system'
                                    ? firstActionRef
                                    : undefined
                                }
                                type="button"
                                className={classes.themeButton}
                                aria-label={`${label} тема`}
                                aria-pressed={preference === value}
                                onClick={() => setThemePreference(value)}
                            >
                                <Icon aria-hidden="true" />
                                <span>{label}</span>
                            </button>
                        ))}
                    </div>
                    <small className={classes.hint}>Сохраняется в этом браузере</small>
                </div>
                <button
                    ref={lastActionRef}
                    type="button"
                    className={classes.logoutButton}
                    aria-label="Выход"
                    onClick={() => {
                        panelRef.current?.hide();
                        logout();
                    }}
                >
                    <LuLogOut aria-hidden="true" />
                    <span>Выйти из аккаунта</span>
                </button>
            </OverlayPanel>
        </>
    );
};
